import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NoRecordFound from '../NoRecordFound';

const DataStateHandler = ({
  loading = false,
  loadingMessage = '',
  renderLoading = undefined,
  error = '',
  errorTitle = '',
  onRetry = undefined,
  retryLabel = 'Retry',
  isEmpty = false,
  emptyTitle = 'No data found',
  emptyMessage = 'Try updating your filters or search criteria.',
  emptyActionLabel = '',
  onEmptyAction = undefined,
  renderEmpty = undefined,
  children,
}) => {
  if (loading) {
    if (renderLoading) {
      return <>{renderLoading()}</>;
    }

    return (
      <Paper
        elevation={0}
        sx={{
          py: 6,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          borderRadius: 2,
        }}
      >
        <CircularProgress />
        {loadingMessage && (
          <Typography variant="body1" color="text.secondary">
            {loadingMessage}
          </Typography>
        )}
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.light',
          backgroundColor: 'rgba(244, 67, 54, 0.04)',
        }}
      >
        <ErrorOutlineIcon color="error" fontSize="large" />
        <Box textAlign="center">
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {errorTitle || 'Something went wrong'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Box>
        {onRetry && (
          <Button
            variant="contained"
            color="error"
            onClick={onRetry}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {retryLabel}
          </Button>
        )}
      </Paper>
    );
  }

  if (isEmpty) {
    if (renderEmpty) {
      return <>{renderEmpty()}</>;
    }

    return (
      <Box sx={{ p: 1 }}>
        <NoRecordFound 
          title={emptyTitle}
          message={emptyMessage}
        />
        {onEmptyAction && emptyActionLabel && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onEmptyAction}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {emptyActionLabel}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return <>{children}</>;
};

DataStateHandler.propTypes = {
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  renderLoading: PropTypes.func,
  error: PropTypes.string,
  errorTitle: PropTypes.string,
  onRetry: PropTypes.func,
  retryLabel: PropTypes.string,
  isEmpty: PropTypes.bool,
  emptyTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
  emptyActionLabel: PropTypes.string,
  onEmptyAction: PropTypes.func,
  renderEmpty: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export default DataStateHandler;

